/**
 * Model Selector Component - Glass Morphism Design
 * Dropdown for selecting LLM model and provider
 * Validates: Requirements 13.1, 13.2
 */
import { useState, useRef, useEffect } from 'react';
import { LLMProvider } from '../../types';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

export interface ModelOption {
  id: string;
  name: string;
  provider: LLMProvider;
  contextWindow?: number;
}

// Available models grouped by provider
export const AVAILABLE_MODELS: ModelOption[] = [
  // OpenAI
  { id: 'gpt-4o', name: 'GPT-4o', provider: 'openai', contextWindow: 128000 },
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini', provider: 'openai', contextWindow: 128000 },
  { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', provider: 'openai', contextWindow: 128000 },
  { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', provider: 'openai', contextWindow: 16385 },
  
  // Anthropic
  { id: 'claude-3-5-sonnet-20241022', name: 'Claude 3.5 Sonnet', provider: 'anthropic', contextWindow: 200000 },
  { id: 'claude-3-opus-20240229', name: 'Claude 3 Opus', provider: 'anthropic', contextWindow: 200000 },
  { id: 'claude-3-haiku-20240307', name: 'Claude 3 Haiku', provider: 'anthropic', contextWindow: 200000 },
  
  // Google
  { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', provider: 'google', contextWindow: 1000000 },
  { id: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash', provider: 'google', contextWindow: 1000000 },
  { id: 'gemini-pro', name: 'Gemini Pro', provider: 'google', contextWindow: 32000 },
  
  // Grok
  { id: 'grok-2', name: 'Grok 2', provider: 'grok', contextWindow: 131072 },
  { id: 'grok-2-mini', name: 'Grok 2 Mini', provider: 'grok', contextWindow: 131072 },
  
  // Meta
  { id: 'llama-3.1-405b-instruct', name: 'LLaMA 3.1 405B', provider: 'meta', contextWindow: 128000 },
  { id: 'llama-3.1-70b-instruct', name: 'LLaMA 3.1 70B', provider: 'meta', contextWindow: 128000 },
  { id: 'llama-3.1-8b-instruct', name: 'LLaMA 3.1 8B', provider: 'meta', contextWindow: 128000 },
  
  // DeepSeek
  { id: 'deepseek-chat', name: 'DeepSeek Chat', provider: 'deepseek', contextWindow: 64000 },
  { id: 'deepseek-coder', name: 'DeepSeek Coder', provider: 'deepseek', contextWindow: 64000 },
  
  // Qwen
  { id: 'qwen-max', name: 'Qwen Max', provider: 'qwen', contextWindow: 32000 },
  { id: 'qwen-plus', name: 'Qwen Plus', provider: 'qwen', contextWindow: 32000 },
  { id: 'qwen-turbo', name: 'Qwen Turbo', provider: 'qwen', contextWindow: 8000 },
];

// Provider display names and badge colors
const PROVIDER_INFO: Record<LLMProvider, { name: string; color: string }> = {
  openai: { name: 'OpenAI', color: '#10b981' }, // green
  anthropic: { name: 'Anthropic', color: '#f97316' }, // orange
  google: { name: 'Google', color: '#3b82f6' }, // blue
  grok: { name: 'Grok', color: '#a855f7' }, // purple
  meta: { name: 'Meta', color: '#6366f1' }, // indigo
  deepseek: { name: 'DeepSeek', color: '#06b6d4' }, // cyan
  qwen: { name: 'Qwen', color: '#ec4899' }, // pink
};

interface ModelSelectorProps {
  selectedModel: string;
  selectedProvider: LLMProvider;
  onSelect: (model: string, provider: LLMProvider) => void;
  disabled?: boolean;
}

export function ModelSelector({
  selectedModel,
  selectedProvider,
  onSelect,
  disabled = false,
}: ModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedModelInfo = AVAILABLE_MODELS.find((m) => m.id === selectedModel);
  const providerInfo = PROVIDER_INFO[selectedProvider];

  // Group models by provider
  const modelsByProvider = AVAILABLE_MODELS.reduce((acc, model) => {
    if (!acc[model.provider]) {
      acc[model.provider] = [];
    }
    acc[model.provider].push(model);
    return acc;
  }, {} as Record<LLMProvider, ModelOption[]>);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all duration-200 ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:border-white/20'
        }`}
        style={{
          backgroundColor: THEME.glassLight,
          borderColor: isOpen ? THEME.borderHover : THEME.border,
          color: THEME.textMain,
          backdropFilter: 'blur(10px)',
        }}
      >
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: providerInfo.color }}
        />
        <span>{selectedModelInfo?.name || selectedModel}</span>
        <Icons.ChevronDown
          size={16}
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: THEME.textMuted }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute top-full left-0 mt-2 w-80 rounded-2xl border overflow-hidden z-50 max-h-96 overflow-y-auto"
          style={{
            backgroundColor: THEME.glassSurface,
            backdropFilter: 'blur(20px)',
            borderColor: THEME.border,
            boxShadow: THEME.shadowXl,
          }}
        >
          {/* Dot pattern overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
              backgroundSize: '12px 12px',
            }}
          />

          <div className="relative">
            {Object.entries(modelsByProvider).map(([provider, models]) => {
              const info = PROVIDER_INFO[provider as LLMProvider];
              return (
                <div key={provider}>
                  {/* Provider Header */}
                  <div
                    className="px-4 py-2 text-xs font-semibold uppercase tracking-wider sticky top-0"
                    style={{
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      backdropFilter: 'blur(10px)',
                      color: THEME.textMuted,
                      borderBottom: `1px solid ${THEME.border}`,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: info.color }}
                      />
                      <span>{info.name}</span>
                    </div>
                  </div>

                  {/* Model Options */}
                  {models.map((model) => (
                    <button
                      key={model.id}
                      type="button"
                      onClick={() => {
                        onSelect(model.id, model.provider);
                        setIsOpen(false);
                      }}
                      className="w-full px-4 py-3 text-left text-sm transition-all duration-200"
                      style={{
                        backgroundColor:
                          model.id === selectedModel ? THEME.accentSoft : 'transparent',
                        borderLeft:
                          model.id === selectedModel
                            ? `2px solid ${THEME.accentColor}`
                            : '2px solid transparent',
                        color: model.id === selectedModel ? THEME.accentColor : THEME.textMain,
                      }}
                      onMouseEnter={(e) => {
                        if (model.id !== selectedModel) {
                          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (model.id !== selectedModel) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{model.name}</span>
                        {model.contextWindow && (
                          <span
                            className="text-xs px-2 py-0.5 rounded-md"
                            style={{
                              backgroundColor: 'rgba(255,255,255,0.05)',
                              color: THEME.textMuted,
                            }}
                          >
                            {formatContextWindow(model.contextWindow)}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function formatContextWindow(tokens: number): string {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(0)}M`;
  }
  return `${(tokens / 1000).toFixed(0)}K`;
}
